import React from "react";
import { motion } from "framer-motion";
import { Download, FileText, Image, Video, Archive, File, ExternalLink } from "lucide-react";
import Footer from "../../Components/Footer";
import { useLanguage } from "../../contexts/LanguageContext";

export default function FilesSection({ onNavigate, onOpenPrivacyPolicy, onOpenCookiePolicy, onOpenTermsOfService }) {
  const { t } = useLanguage();
  const containerAnimation = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemAnimation = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
  };

  const files = [
    // ELLEGI
    {
      nameKey: "filePres",
      descKey: "filePresDesc",
      type: "PDF",
      size: "1.3 MB",
      icon: FileText,
      downloadUrl: "./downloads/ELLEGI_Presentazione_26Set2025.pdf",
      category: "Ellegi",
      isDirectDownload: true
    },
    {
      nameKey: "filePresOutdoor",
      descKey: "filePresOutdoorDesc",
      type: "PDF",
      size: "0.946 MB",
      icon: FileText,
      downloadUrl: "./downloads/ELLEGI_Presentazione_30Set2025 - Proposte Outdoor.pdf",
      category: "Ellegi",
      isDirectDownload: true
    },
    {
      nameKey: "fileCollImg",
      descKey: "fileCollImgDesc",
      type: "PDF",
      size: "3.2 MB",
      icon: FileText,
      downloadUrl: "./downloads/Collaborazioni ELLEGI_09Lug25_COMPLETA CON IMMAGINI.pdf",
      category: "Ellegi",
      isDirectDownload: true
    },
    {
      nameKey: "fileCollNoImg",
      descKey: "fileCollNoImgDesc",
      type: "PDF",
      size: "0.3777 MB",
      icon: FileText,
      downloadUrl: "./downloads/Collaborazioni ELLEGI_09Lug25_senza IMMAGINI.pdf",
      category: "Ellegi",
      isDirectDownload: true
    },

    // Intra Lighting
    {
      nameKey: "fileCatIntra",
      descKey: "fileCatIntraDesc",
      type: "Link",
      size: "-",
      icon: ExternalLink,
      downloadUrl: "https://www.intra-lighting.com/novelties-brochure/it",
      category: "Intra Lighting",
      isDirectDownload: false
    },

    // CARIBONI
    {
      nameKey: "fileCatCariboni",
      descKey: "fileCatCariboniDesc",
      type: "Link",
      size: "-",
      icon: ExternalLink,
      downloadUrl: "https://www.caribonigroup.com/it/prodotti/catalogo",
      category: "Cariboni Group",
      isDirectDownload: false
    },

    // RIDI Italia
    {
      nameKey: "fileCatRidi",
      descKey: "fileCatRidiDesc",
      type: "Link",
      size: "-",
      icon: ExternalLink,
      downloadUrl: "https://www.ridi-italia.it/download/",
      category: "RIDI Italia",
      isDirectDownload: false
    },

    // Wieland Electric
    {
      nameKey: "fileCatWieland",
      descKey: "fileCatWielandDesc",
      type: "Link",
      size: "-",
      icon: ExternalLink,
      downloadUrl: "https://www.wieland-electric.it/it/prodotti/",
      category: "Wieland Electric",
      isDirectDownload: false
    },

    // Leitner Energy
    {
      nameKey: "fileCatLeitner",
      descKey: "fileCatLeitnerDesc",
      type: "Link",
      size: "-",
      icon: ExternalLink,
      downloadUrl: "https://www.leitnerenergy.it/",
      category: "Leitner Energy",
      isDirectDownload: false
    },

    // Volatiles
    {
      nameKey: "fileCatVolatiles",
      descKey: "fileCatVolatilesDesc",
      type: "Link",
      size: "-",
      icon: ExternalLink,
      downloadUrl: "https://volatiles.lighting/",
      category: "Volatiles",
      isDirectDownload: false
    }
  ];

  const categories = [
    "Tutti",
    "Ellegi",
    "Intra Lighting",
    "Cariboni Group",
    "RIDI Italia",
    "Wieland Electric",
    "Leitner Energy",
    "Volatiles"
  ];
  const [selectedCategory, setSelectedCategory] = React.useState("Tutti");

  const filteredFiles = selectedCategory === "Tutti"
    ? files
    : files.filter(file => file.category === selectedCategory);

  const getFileIcon = (file) => {
    const IconComponent = file.icon;
    return <IconComponent className="w-8 h-8 text-[--accent]" />;
  };

  const handleDownload = (file) => {
    if (file.isDirectDownload) {
      // Download diretto per file Ellegi
      window.open(file.downloadUrl, '_blank');
    } else {
      // Apri link esterno per altri partner
      window.open(file.downloadUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <>
      <section className="min-h-screen py-24 md:py-32 bg-[--bg-secondary] relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[--accent] rounded-full opacity-5 blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500 rounded-full opacity-5 blur-3xl" />
        </div>

        <motion.div
          variants={containerAnimation}
          initial="hidden"
          animate="visible"
          className="max-w-7xl mx-auto px-6 relative z-10"
        >
          {/* Header */}
          <motion.div variants={itemAnimation} className="text-center mb-16">
            <motion.div className="inline-flex items-center justify-center mb-6">
              <div className="w-12 h-px bg-gradient-to-r from-transparent via-[--accent] to-transparent" />
              <span className="text-[--accent] text-sm font-medium tracking-[0.3em] uppercase mx-6">
                06
              </span>
              <div className="w-12 h-px bg-gradient-to-r from-transparent via-[--accent] to-transparent" />
            </motion.div>

            <h2 className="text-4xl md:text-6xl font-display font-bold text-[--text-primary] mb-8 leading-tight">
              {t.files.title}
            </h2>
            <p className="text-lg text-[--text-secondary] max-w-3xl mx-auto">
              {t.files.subtitle}
            </p>
          </motion.div>

          {/* Category Filter */}
          <motion.div variants={itemAnimation} className="mb-12">
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 ${selectedCategory === category
                    ? 'bg-[--accent] text-black shadow-lg'
                    : 'bg-[--bg-primary] text-[--text-secondary] hover:bg-[--accent]/20 hover:text-[--accent]'
                    }`}
                >
                  {category === "Tutti" ? t.files.all : category}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Files Grid */}
          <motion.div variants={itemAnimation}>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredFiles.map((file, index) => (
                <motion.div
                  key={file.nameKey}
                  variants={itemAnimation}
                  className="group"
                  whileHover={{ y: -8, scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                >
                  <div className="relative glass rounded-3xl p-6 h-full hover:shadow-luxury-hover transition-all duration-500 border border-transparent hover:border-[--accent]/30 overflow-hidden">
                    {/* Background gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-[--accent]/5 via-transparent to-[--accent]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                    {/* Restricted badge */}
                    {file.restricted && (
                      <div className="absolute top-4 right-4 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                        {t.files.restricted}
                      </div>
                    )}

                    {/* File Icon */}
                    <div className="relative w-16 h-16 rounded-2xl bg-[--accent]/10 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-500">
                      {getFileIcon(file)}
                    </div>

                    <div className="text-center mb-4 relative z-10">
                      <h4 className="text-lg font-display font-bold text-[--text-primary] mb-2 group-hover:text-[--accent] transition-colors duration-300">
                        {t.files[file.nameKey]}
                      </h4>
                      <p className="text-[--text-secondary] text-sm leading-relaxed mb-3 group-hover:text-[--text-primary] transition-colors duration-300">
                        {t.files[file.descKey]}
                      </p>

                      {/* File Info */}
                      <div className="flex items-center justify-center space-x-4 text-xs text-[--text-secondary] mb-4">
                        <span className="bg-[--accent]/20 px-2 py-1 rounded-full">
                          {file.type}
                        </span>
                        <span>{file.size}</span>
                      </div>
                    </div>

                    {/* Download/Link Button */}
                    <motion.button
                      onClick={() => handleDownload(file)}
                      className={`w-full ${file.isDirectDownload
                        ? 'bg-gradient-to-r from-[--accent] to-yellow-400 text-black'
                        : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white'
                        } py-3 px-4 rounded-xl font-semibold flex items-center justify-center space-x-2 hover:shadow-lg transition-all group-hover:scale-105 duration-300`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {file.isDirectDownload ? (
                        <>
                          <Download className="w-4 h-4" />
                          <span>{t.files.download}</span>
                        </>
                      ) : (
                        <>
                          <ExternalLink className="w-4 h-4" />
                          <span>{t.files.goToSite}</span>
                        </>
                      )}
                    </motion.button>

                    {/* Bottom accent line */}
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[--accent] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Info Section */}
          <motion.div variants={itemAnimation} className="mt-16">
            <div className="glass rounded-3xl p-8 text-center">
              <h3 className="text-2xl font-display font-semibold text-[--text-primary] mb-4">
                {t.files.needDocsTitle}
              </h3>
              <p className="text-[--text-secondary] mb-6">
                {t.files.needDocsDesc}
              </p>
              <motion.button
                onClick={() => {
                  // Navigate to contacts section
                  const contactsSection = document.getElementById('contacts');
                  if (contactsSection) {
                    contactsSection.scrollIntoView({ behavior: 'smooth' });
                  } else {
                    window.location.hash = '#contacts';
                  }
                }}
                className="inline-flex items-center bg-[--accent] hover:bg-[--accent-hover] text-black px-8 py-3 rounded-xl font-semibold transition-all group"
                whileHover={{ scale: 1.05 }}
              >
                <span>{t.files.contactUs}</span>
                <ExternalLink className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </motion.button>
            </div>
          </motion.div>
        </motion.div>

        {/* Gradient Footer */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[--bg-primary] via-[--bg-primary]/60 to-transparent pointer-events-none" />
      </section>

      <Footer
        onNavigate={onNavigate}
        onOpenPrivacyPolicy={onOpenPrivacyPolicy}
        onOpenCookiePolicy={onOpenCookiePolicy}
        onOpenTermsOfService={onOpenTermsOfService}
      />
    </>
  );
}
